import React, { useEffect, useState } from "react";
import { ColumnConfig } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { JobStatusesResult } from "@app/operations/jobStatisticsResult";
import OperationsService from "@app/operations/operationsService";
import { FlyoutLink } from "@app/shared/Flyouts";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { getMostRecentWednesday } from "@app/shared/utility/getMostRecentDay";
import { FullJobDetailsFlyoutContent } from "@app/operations/FullJobDetailsFlyoutContent";
import UnfinishedProjectsFlyoutContent from "@app/operations/UnfinishedProjectsFlyoutContent";

const operationsService = new OperationsService();
const ALL_LINES_OF_BUSINESS = "all";
const OTHER_LINES_OF_BUSINESS = "other";
const ALL_JOB_TYPES = 0;
const JOB_TYPES = [
  { label: "1001 - Custom Adequacy", value: 1001 },
  { label: "1002 - Custom Adequacy Region Summary", value: 1002 },
];
const JOB_STATUS_ENUM = [
  "Total",
  "Cancelled",
  "UnCancelled",
  "Successful",
  "Running",
  "Failed",
  "Failed With Retries Remaining"
];

const projectStatisticsColumns: ColumnConfig = {
  lineOfBusiness: {
    displayText: "Line Of Business",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__unfinished-project-line-of-business-output",
  },
  projectCount: {
    displayText: "Project Count",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__unfinished-project-count-output",
  },
};

const jobStatisticsColumns: ColumnConfig = {
  jobCount: {
    displayText: "Job Count",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__job-count-output",
  },
  jobType: {
    displayText: "Job Type",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__job-type-output",
  },
  lineOfBusiness: {
    displayText: "Line Of Business",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__line-of-business-output",
  },
  status: {
    displayText: "Status",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__status-output",
  },
};

interface JobStatusesProps {
  clients: { clientId: number; name: string }[];
  clientId: number | null;
  setClientId: React.Dispatch<React.SetStateAction<number | null>>;
}

const JobStatuses: React.FC<JobStatusesProps> = ({
  clients,
  clientId,
  setClientId,
}) => {
  const [data, setData] = useState<JobStatusesResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [runDate, setRunDate] = useState<string>(getMostRecentWednesday());
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<string[]>([]);
  const [lineOfBusiness, setLineOfBusiness] = useState<string>(ALL_LINES_OF_BUSINESS);
  const [jobType, setJobType] = useState<number>(ALL_JOB_TYPES);
  const [fetchLocked, setFetchLocked] = useState<boolean>(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>();

  useEffect(() => {
    resetFormValues();
    if (clientId == null) {
      return;
    }

    operationsService
      .getLinesOfBusiness(clientId, true)
      .then(linesOfBusiness => setLineOfBusinessOptions(linesOfBusiness));
  }, [clientId]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setData(null);
    setLoading(true);
    setError(null);
    setFetchLocked(true);

    try {
      const adjustedLineOfBusiness = lineOfBusiness === ALL_LINES_OF_BUSINESS ? "" : lineOfBusiness;
      const result = await operationsService.getJobStatuses(
        clientId,
        runDate,
        adjustedLineOfBusiness,
        jobType
      );
      const filteredResult = selectedStatusFilter ? result.jobStatuses.filter(item => (item.status.toUpperCase() === selectedStatusFilter)) : result.jobStatuses;
      result.jobStatuses = filteredResult;

      setData(result);
    } catch (err) {
      setError("Error fetching job statistics by Line of Business");
    } finally {
      setLoading(false);
    }
  };

  const resetFormValues = () => {
    setError(null);
    setLineOfBusinessOptions([]);
    setLineOfBusiness(ALL_LINES_OF_BUSINESS);
    setJobType(ALL_JOB_TYPES);
    setData(null);
    setFetchLocked(false);
  };

  function handleFilterChange<T>(setMethod: React.Dispatch<React.SetStateAction<T>>, newValue: T): void {
    setMethod(oldValue => {
      if (oldValue != newValue) {
        setFetchLocked(false);
      }
      return newValue;
    });
  }

  return (
    <div>
      <div className="form-row mb-3">
        <div className="col pe-1">
          <QESSelect
            label="Client"
            value={clientId !== null ? clientId.toString() : ""}
            onChange={(event) => setClientId(Number(event.target.value))}
            testId="weekend-run-monitoring__job-statistics-by-line-of-business-client-select"
            placeholder="Select Client"
            required
            disabled={clients.length === 0}
            descriptiveText={clients.length === 0 ? "Loading Clients..." : ""}
            isValid={clientId !== null}
          >
            {clients.map(client => (
              <option key={client.clientId} value={client.clientId}>
                {client.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESSelect
            label="Line of Business"
            value={lineOfBusiness}
            onChange={e => handleFilterChange(setLineOfBusiness, e.target.value)}
            testId="weekend-run-monitoring__line-of-business-select"
            disabled={clientId === null}
          >
            <option value={ALL_LINES_OF_BUSINESS}>All</option>
            {lineOfBusinessOptions.map(lob => (
              <option key={lob.toLowerCase()} value={lob.toLowerCase()}>
                {lob.toUpperCase()}
              </option>
            ))}
            <option value={OTHER_LINES_OF_BUSINESS}>Other</option>
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESSelect
            label="Job Type"
            value={jobType}
            onChange={e => handleFilterChange(setJobType, Number(e.target.value))}
            testId="weekend-run-monitoring__job-type-select"
            disabled={clientId === null}
          >
            <option value={ALL_JOB_TYPES}>All</option>
            {JOB_TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESDateInput
            label="Run Date"
            value={runDate}
            onChange={e => handleFilterChange(setRunDate, e.target.value)}
            testId="weekend-run-monitoring__run-date-input"
          />
        </div>
        <div className="col px-1">
          <QESSelect
            allowEmpty={true}
            containerClassName="jobs-list__requeue-job-queue-list"
            testId="jobs-list__requeue-list"
            label="Job Status Filter"
            placeholder="Select a Status Filter"
            onChange={e => handleFilterChange(setSelectedStatusFilter, e.target.value)}>
            {Object.values(JOB_STATUS_ENUM).map(item => (
              <option key={item}>
                {item.toUpperCase()}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col d-flex align-items-end ps-1">
          <TooltipWrapper position="top" text={clientId === null ? "Please select a client." : (fetchLocked ? "Change the filters to calculate new results." : "")}>
            <QESButton onClick={fetchData} qesType="primary" qesTestId="weekend-run-monitoring__calculate-job-statistics-button" disabled={clientId === null || fetchLocked}>
              Search
            </QESButton>
          </TooltipWrapper>
        </div>
      </div>
      {loading && <LoadingSpinner />}
      {error && <div>{error}</div>}
      {data && (
        <div className="row">
          <div className="col-lg-4 mb-4">
            <h5 className="fw-normal">Unfinished Projects</h5>
            <Waffles
              columns={projectStatisticsColumns}
              data={data.projectStatuses.map((stat, index) => ({
                key: index.toString(),
                lineOfBusiness: stat.lineOfBusiness.toUpperCase(),
                projectCount: (
                  <FlyoutLink
                    classNames="p-0 qes-button-secondary"
                    content={{
                      body: (
                        <UnfinishedProjectsFlyoutContent
                          runDate={runDate}
                          clientId={clientId}
                          lineOfBusiness={stat.lineOfBusiness}
                        />
                      ),
                      title: `Unfinished Projects for ${stat.lineOfBusiness}`,
                    }}
                    testId={`weekend-run-monitoring__unfinished-projects__flyout-${index}`}
                    size="60"
                  >
                    {stat.projectCount}
                  </FlyoutLink>
                ),
              }))}
              enablePagination={false}
              passThroughSorting={true}
              testId="weekend-run-monitoring__unfinished-projects-table"
            />
          </div>
          <div className="col-8">
            <h5 className="fw-normal">Job Statistics</h5>
            <Waffles
              /* oxlint-disable sort-keys */
              columns={jobStatisticsColumns}
              data={data.jobStatuses.map((stat, index) => ({
                key: index.toString(),
                status: (
                  <FlyoutLink
                    classNames="p-0 qes-button-secondary"
                    content={{
                      title: `Full Job Details for ${stat.lineOfBusiness}`,
                      body: (
                        <FullJobDetailsFlyoutContent
                          clientId={clientId}
                          runDate={runDate}
                          jobStatus={stat.status}
                          lineOfBusiness={stat.lineOfBusiness}
                          jobType={stat.jobType}
                        />
                      ),
                    }}
                    testId={`weekend-run-monitoring__full-job-details-flyout-${index}`}
                    size="80"
                  >
                    {stat.status}
                  </FlyoutLink>
                ),
                lineOfBusiness: stat.lineOfBusiness,
                jobType: stat.jobType,
                jobCount: stat.jobCount,
              }))}
              enablePagination={false}
              passThroughSorting={true}
              testId="weekend-run-monitoring__job-statistics-by-line-of-business-table"
            /* oxlint-enable sort-keys */
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default JobStatuses;